import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import MobileCTA from "@/components/site/MobileCTA";
import ScrollScanner from "@/components/ScrollScanner";
import BookingForm from "@/components/site/BookingForm";
import { Phone, MapPin, Clock } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { trackPhoneCallLead } from "@/lib/ads";
import SectionHeader from "@/components/site/SectionHeader";
import Reveal from "@/components/site/Reveal";
import RelatedLinks from "@/components/site/RelatedLinks";
import { useSeo } from "@/lib/useSeo";

const contactMethods = [
  {
    icon: Phone,
    label: "Call or Text",
    value: BRAND.phoneDisplay,
    href: `tel:${BRAND.phoneTel}`,
  },
  {
    icon: MapPin,
    label: "Service Area",
    value: "Vancouver & the Lower Mainland, BC",
    href: "#areas",
  },
  {
    icon: Clock,
    label: "Booking",
    value: "Morning, Afternoon or Anytime",
    href: "#contact-form",
  },
];

export default function Contact() {
  useSeo({
    title: "Contact Meridian | Book a Bed Bug Treatment",
    description: "Contact Meridian to book a discreet bed bug treatment in Vancouver or ask a question — call or text us, or send a few property details through our booking form.",
    path: "/contact",
  });
  return (
    <div className="overflow-x-hidden pb-16 lg:pb-0">
      <ScrollScanner />
      <Navbar />
      <main id="main-content">
        <section className="bg-background px-5 pt-32 pb-20 lg:px-8 lg:pt-40 lg:pb-28">
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-accent" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">Contact</span>
              </div>
              <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-primary sm:text-5xl lg:text-[3.4rem]">Talk to Meridian.</h1>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">Reach out to book a bed bug treatment or ask a question. We respond quickly and discretely.</p>
            </Reveal>
            <div className="mt-12 grid gap-4 sm:grid-cols-3">
              {contactMethods.map((m) => {
                const Icon = m.icon;
                return (
                  <Reveal key={m.label}>
                    <a
                      href={m.href}
                      onClick={m.label === "Call or Text" ? trackPhoneCallLead : undefined}
                      className="block h-full rounded-lg border border-border bg-muted/30 p-6 transition-colors hover:border-accent/50"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent">
                        <Icon className="h-5 w-5" />
                      </span>
                      <h2 className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{m.label}</h2>
                      <p className="mt-2 font-heading text-lg font-bold text-primary">{m.value}</p>
                    </a>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section id="contact-form" className="border-t border-border bg-muted/30 px-5 py-20 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <SectionHeader
                kicker="Booking Form"
                title="REQUEST A TREATMENT."
                subtitle="Share a few details and we'll contact you to confirm availability and treatment requirements."
              />
            </Reveal>
            <div className="mt-10">
              <BookingForm />
            </div>
          </div>
        </section>
        <RelatedLinks links={[
          { to: "/bed-bug-treatment-vancouver", label: "Guide", title: "Bed Bug Treatment in Vancouver", desc: "Learn the signs, how treatment works, and what to prepare before a visit.", cta: "Read the guide" },
          { to: "/about", label: "About", title: "About Meridian", desc: "A specialist bed bug service built around one problem and one discipline.", cta: "Read more" },
        ]} />
      </main>
      <Footer />
      <MobileCTA />
    </div>
  );
}